'use client';

import { useEffect, useState } from 'react';
import Script from 'next/script';
import { usePathname } from 'next/navigation';
import { isPublicMarketingPath } from '@/lib/appRoutes';

/**
 * Google Analytics 4 — PUBLIC MARKETING SURFACES ONLY.
 *
 * Why a gate instead of dropping the tag in the root layout: the same Next app
 * serves the signed-in ERP. Those URLs carry tenant record ids (/sites/<id>,
 * /leads/<id>), and shipping them to Google would hand a third party a map of
 * our customers' business data. So the tag is only ever loaded from a public
 * page, and the kill switch (`ga-disable-<id>`) is flipped the moment
 * navigation leaves one — a client-side route change can't unload a script
 * that's already running, but it can switch it off.
 *
 * Also off by design:
 *  - Google Signals / ad personalisation — we sell software, not ads, and the
 *    cookie policy promises no advertising cookies.
 *  - Automatic page_view. We send it ourselves, per allowed route, so SPA
 *    navigations are measured and private ones never are.
 *
 * The measurement id is public by nature (it ships in the page source), so it
 * is baked in rather than left to a deploy-time variable someone forgets to
 * set. The env var still wins: point a staging deploy at another stream, or
 * set it empty to turn measurement off completely. Development never reports
 * unless you opt in — localhost hits would otherwise pollute the property.
 */
const GA_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID
  ?? (process.env.NODE_ENV === 'production' ? 'G-3FYSKWK4EL' : '');

/**
 * Which paths may be measured — `isPublicMarketingPath` in src/lib/appRoutes.ts,
 * derived from the same lists that build robots.txt and the noindex header, so
 * a new module can't be measured by accident the way a hand-kept allowlist
 * would eventually let happen. That list already covers the dashboard, the
 * operator console, the client portal and the one-time-token pages
 * (set-password, reset-password, /q) — exactly the URLs that must never reach
 * Google.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag?: (...args: any[]) => void;
  }
}

export default function GoogleAnalytics() {
  const pathname = usePathname() || '/';
  const allowed = !!GA_ID && isPublicMarketingPath(pathname);
  const [ready, setReady] = useState(false);
  /* Once the tag is on the page it stays mounted: next/script won't re-run an
   * id it has already executed, so unmounting would leave us with a loaded but
   * untracked tag. Disabled, not removed, on private routes. */
  const [tagMounted, setTagMounted] = useState(allowed);

  useEffect(() => {
    if (!GA_ID) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (allowed && !tagMounted) setTagMounted(true);
    // Hard kill switch — gtag checks this on every hit, so an app route visited
    // after a marketing page sends nothing at all.
    (window as unknown as Record<string, boolean>)[`ga-disable-${GA_ID}`] = !allowed;
    if (!allowed || !ready || typeof window.gtag !== 'function') return;
    window.gtag('event', 'page_view', {
      page_path: pathname + window.location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, allowed, ready, tagMounted]);

  // Never ship the tag to a browser that has only been on private routes.
  if (!GA_ID || !tagMounted) return null;

  return (
    <>
      <Script
        id="ga4-src"
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}
      />
      <Script id="ga4-init" strategy="afterInteractive" onReady={() => setReady(true)}>
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = window.gtag || gtag;
          gtag('js', new Date());
          gtag('config', '${GA_ID}', {
            send_page_view: false,
            anonymize_ip: true,
            allow_google_signals: false,
            allow_ad_personalization_signals: false
          });
        `}
      </Script>
    </>
  );
}
